import { getUserFacts } from "@/lib/core/user-facts.server.ts";
import { createServerFn, createServerOnlyFn } from "@tanstack/react-start";
import { PROFILE_FACT_KEYS } from "@wags/profile-contracts/profile-fact-keys.contract.ts";
import {
    requireViewer,
    resolveViewerProfileIdentity,
    resolveViewerRef,
} from "@/lib/core/auth";
import type ISeedViewerLocationFactDependencies from "./location-fact-dependencies.interface";
import {
    type ViewerLocation,
    ViewerLocationSchema,
} from "./viewer-location.contract";
import { rememberLocationSelection } from "./selected-location";

export async function seedViewerLocationFactWithDependencies(
    dependencies: ISeedViewerLocationFactDependencies,
    location: ViewerLocation,
    source: "vercel" | "ipapi",
) {
    const logFailure = (error: Error) => {
        console.error("Failed to seed viewer location fact", {
            error,
            source,
        });
    };
    const viewer = await dependencies.resolveViewer().catch((error) => {
        logFailure(error);
        return null;
    });
    if (!viewer) return;
    const userFacts = await dependencies.getUserFacts().catch((error) => {
        logFailure(error);
        return null;
    });
    if (!userFacts) return;
    await userFacts
        .saveIfAbsent({
            userProfileId: viewer.viewerId,
            facts: [
                {
                    factKey: PROFILE_FACT_KEYS.location,
                    value: ViewerLocationSchema.parse(location),
                    source: "inferred",
                    note: `Approximate location detected from ${source}.`,
                },
            ],
        })
        .catch(logFailure);
}

export const seedViewerLocationFact = createServerOnlyFn(
    async (location: ViewerLocation, source: "vercel" | "ipapi") =>
        await seedViewerLocationFactWithDependencies(
            {
                resolveViewer: resolveViewerRef,
                getUserFacts: async () => await getUserFacts(),
            },
            location,
            source,
        ),
);

export const saveViewerLocationFact = createServerFn({ method: "POST" })
    .inputValidator(ViewerLocationSchema)
    .handler(async ({ data }) => {
        const [viewer, userFacts] = await Promise.all([
            resolveViewerProfileIdentity(),
            getUserFacts(),
        ]);
        const resolvedViewer = requireViewer(viewer);
        await userFacts.save({
            userProfileId: resolvedViewer.appUserId,
            facts: [
                {
                    factKey: PROFILE_FACT_KEYS.location,
                    value: data,
                    source: "user",
                    note: "Location selected by the user.",
                },
            ],
        });
    });

export function persistLocationSelection(location: ViewerLocation) {
    rememberLocationSelection(location);
    void saveViewerLocationFact({ data: location }).catch((error) => {
        console.error("Failed to save viewer location fact", { error });
    });
}
